import chalk from 'chalk';
import clack from '../utils/clack.js';
import { getConfig, saveConfig, isUnclaimedEnvironment } from '../lib/config-store.js';
import type { CliConfig } from '../lib/config-store.js';
import { outputSuccess, outputJson, exitWithError, isJsonMode } from '../utils/output.js';
import { isAgentMode, isCiMode, isPromptAllowed } from '../utils/interaction-mode.js';
import { missingArgsRecovery } from '../utils/recovery-hints.js';
import { formatWorkOSCommand } from '../utils/command-invocation.js';
import { ExitCode, exitWithCode } from '../utils/exit-codes.js';

const ENV_NAME_REGEX = /^[a-z0-9\-_]+$/;

function validateEnvName(name: string | undefined): string | undefined {
  if (!name || !ENV_NAME_REGEX.test(name)) {
    return 'Name must contain only lowercase letters, numbers, hyphens, and underscores';
  }
  return undefined;
}

function getOrCreateConfig(): CliConfig {
  return getConfig() ?? { environments: {} };
}

export async function runEnvAdd(options: {
  name?: string;
  apiKey?: string;
  clientId?: string;
  endpoint?: string;
}): Promise<void> {
  let { name, apiKey, endpoint } = options;
  const { clientId } = options;

  if (name && apiKey) {
    // Non-interactive mode
    const nameError = validateEnvName(name);
    if (nameError) {
      exitWithError({ code: 'invalid_args', message: nameError });
    }
  } else if (!isPromptAllowed()) {
    exitWithError({
      code: 'missing_args',
      message: isAgentMode()
        ? `Name and API key required in agent mode. Example: ${formatWorkOSCommand('env add staging sk_test_xxx --client-id client_xxx')}`
        : isCiMode()
          ? 'Name and API key required in CI mode.'
          : 'Name and API key required when prompting is unavailable.',
      recovery: missingArgsRecovery(undefined, 'Provide environment name and API key as positional arguments.'),
    });
  } else {
    // Interactive mode
    const nameResult = await clack.text({
      message: 'Enter a name for the environment (e.g., production, sandbox, local)',
      validate: (value) => validateEnvName(value),
    });
    if (clack.isCancel(nameResult)) exitWithCode(ExitCode.CANCELLED);
    name = nameResult;

    const typeResult = await clack.select({
      message: 'Select the environment type',
      options: [
        { value: 'production', label: 'Production' },
        { value: 'sandbox', label: 'Sandbox' },
      ],
    });
    if (clack.isCancel(typeResult)) exitWithCode(ExitCode.CANCELLED);

    const apiKeyResult = await clack.password({
      message: 'Enter the API key for this environment',
      validate: (value) => {
        if (!value) return 'API key is required';
        return undefined;
      },
    });
    if (clack.isCancel(apiKeyResult)) exitWithCode(ExitCode.CANCELLED);
    apiKey = apiKeyResult;

    const config = getOrCreateConfig();
    const isFirst = Object.keys(config.environments).length === 0;

    config.environments[name] = {
      name,
      type: typeResult as 'production' | 'sandbox',
      apiKey,
      ...(clientId && { clientId }),
      ...(endpoint && { endpoint }),
    };

    if (isFirst) {
      config.activeEnvironment = name;
    }

    saveConfig(config);
    clack.log.success(`Environment ${chalk.bold(name)} added`);
    if (isFirst) {
      clack.log.info(`Set as active environment`);
    }
    return;
  }

  // Non-interactive path
  const config = getOrCreateConfig();
  const isFirst = Object.keys(config.environments).length === 0;

  const type: 'production' | 'sandbox' = apiKey.startsWith('sk_test_') ? 'sandbox' : 'production';

  config.environments[name!] = {
    name: name!,
    type,
    apiKey,
    ...(clientId && { clientId }),
    ...(endpoint && { endpoint }),
  };

  if (isFirst) {
    config.activeEnvironment = name;
  }

  saveConfig(config);
  outputSuccess('Environment added', { name: name!, type, active: isFirst });
}

export async function runEnvRemove(name: string): Promise<void> {
  const config = getConfig();
  if (!config || Object.keys(config.environments).length === 0) {
    exitWithError({
      code: 'no_environments',
      message: 'No environments configured. Run `workos env add` to get started.',
    });
  }

  if (!config.environments[name]) {
    const available = Object.keys(config.environments).join(', ');
    exitWithError({ code: 'not_found', message: `Environment "${name}" not found. Available: ${available}` });
  }

  delete config.environments[name];

  if (config.activeEnvironment === name) {
    const remaining = Object.keys(config.environments);
    config.activeEnvironment = remaining.length > 0 ? remaining[0] : undefined;
    if (config.activeEnvironment && !isJsonMode()) {
      clack.log.info(`Active environment switched to ${chalk.bold(config.activeEnvironment)}`);
    }
  }

  saveConfig(config);
  outputSuccess('Environment removed', { name, newActive: config.activeEnvironment ?? null });
}

export async function runEnvSwitch(name?: string): Promise<void> {
  const config = getConfig();
  if (!config || Object.keys(config.environments).length === 0) {
    exitWithError({
      code: 'no_environments',
      message: 'No environments configured. Run `workos env add` to get started.',
    });
  }

  if (name) {
    if (!config.environments[name]) {
      const available = Object.keys(config.environments).join(', ');
      exitWithError({ code: 'not_found', message: `Environment "${name}" not found. Available: ${available}` });
    }
  } else {
    // Interactive selection (TTY only — non-TTY guard is in bin.ts)
    const options = Object.entries(config.environments).map(([key, env]) => {
      let label = key;
      if (env.type === 'sandbox') label += ` [Sandbox]`;
      if (env.endpoint) label += ` [${env.endpoint}]`;
      if (key === config.activeEnvironment) label += chalk.green(' (active)');
      return { value: key, label };
    });

    const selected = await clack.select({
      message: 'Select an environment',
      options,
    });
    if (clack.isCancel(selected)) exitWithCode(ExitCode.CANCELLED);
    name = selected as string;
  }

  config.activeEnvironment = name;
  saveConfig(config);

  const env = config.environments[name];
  const warnings = process.env.WORKOS_API_KEY
    ? [
        {
          code: 'env_var_override',
          message:
            "WORKOS_API_KEY is set in your shell. It will override this environment's stored key unless you pass --api-key.",
        },
      ]
    : undefined;
  outputSuccess('Switched environment', { name, type: env.type }, { warnings });
}

export async function runEnvList(): Promise<void> {
  const config = getConfig();
  if (!config || Object.keys(config.environments).length === 0) {
    if (isJsonMode()) {
      outputJson({ data: [] });
    } else {
      clack.log.info('No environments configured. Run `workos env add` to get started.');
    }
    return;
  }

  const entries = Object.entries(config.environments);

  if (isJsonMode()) {
    const data = entries.map(([key, env]) => ({
      name: key,
      type: env.type,
      active: key === config.activeEnvironment,
      endpoint: env.endpoint ?? null,
      hasApiKey: !!env.apiKey,
      hasClientId: !!env.clientId,
    }));
    outputJson({ data });
    return;
  }

  // Human-mode table
  const hasUnclaimed = entries.some(([, env]) => isUnclaimedEnvironment(env));
  const nameW =
    Math.max(6, ...entries.map(([k, env]) => k.length + (isUnclaimedEnvironment(env) ? ' (unclaimed)'.length : 0))) + 2;
  const typeW = 12;

  const header = [
    chalk.yellow('  '),
    chalk.yellow('Name'.padEnd(nameW)),
    chalk.yellow('Type'.padEnd(typeW)),
    chalk.yellow('Endpoint'),
  ].join('  ');

  const separator = chalk.dim('─'.repeat(header.length));

  console.log(header);
  console.log(separator);

  for (const [key, env] of entries) {
    const isActive = key === config.activeEnvironment;
    const marker = isActive ? chalk.green('▸ ') : '  ';
    const unclaimed = isUnclaimedEnvironment(env);
    const displayName = unclaimed ? `${key} ${chalk.yellow('(unclaimed)')}` : key;
    const name = isActive ? chalk.green(displayName.padEnd(nameW)) : displayName.padEnd(nameW);
    const type = unclaimed ? 'Unclaimed' : env.type === 'sandbox' ? 'Sandbox' : 'Production';
    const endpoint = env.endpoint || chalk.dim('default');

    console.log([marker, name, type.padEnd(typeW), endpoint].join('  '));
  }

  if (hasUnclaimed) {
    console.log('');
    console.log(chalk.dim('  Run `workos env claim` to keep this environment.'));
  }
}
